import { Component, Vue, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { Alert, Select, Tooltip, Button } from "ant-design-vue";
import Grid from "@/components/wijmo-grid/grid/grid.vue";
import { fmtMsg } from "commonui";
import { CommonLocale, LFSearchReportLocale } from "@/locales/localeid";
import { VersionGroupModule } from "@/modules/versiongroup";
import { disabledDueToAccessLevel, getRouteAccessLevel, isAnyVersionSelected } from "@/util/func";
import { TagModel } from "@/models/title-list-model";
import TitleListState from "@/modules/title-list";
import {
	LFSearchManagerService,
	LFSerachReportRequestParamsModel,
	LFSearchReportResponseModel
} from "@/services/text-guardian/lf-search/index";
import { IWorkbookCell } from "wijmo/wijmo.xlsx";
import PageTitle from "@/components/page-title/page-title.vue";
import { AccessLevel } from "@/util/enum";

@Component({
	components: {
		Grid,
		Select,
		Option: Select.Option,
		Alert,
		Tooltip,
		Button,
		PageTitle
	}
})
export default class LFSearch extends Vue {
	private titleListModule = getModule(TitleListState);
	private versionGroupModule = VersionGroupModule; // For watch need to create local reference
	public noVersion: boolean = false;
	public format = fmtMsg;
	public commonLocale = CommonLocale;
	public locale = LFSearchReportLocale;
	public tagSeachWorker: Worker | undefined;
	public tagSearch: TagModel[] = [];
	public searching = false;
	public tags: TagModel[] = this.titleListModule.tags;
	public searchTerm: string = "";
	public codeSelected: string = "";
	public loading: boolean = false;
	private lfSearchManagerService = LFSearchManagerService();
	public lfSearchReport: LFSearchReportResponseModel[] = [];
	public levels = this.titleListModule.levels;
	public accessLevel = AccessLevel;

	get currentRouteAccessLevel() {
		return getRouteAccessLevel(this.$route);
	}

	get disabledDueToAccessLevel() {
		return disabledDueToAccessLevel(this.currentRouteAccessLevel);
	}

	created() {
		this.tagSeachWorker = new Worker("@/util/workers/worker.ts", { type: "module" });
		this.tagSeachWorker.onmessage = (event) => {
			this.tagSearch = event.data;
			this.searching = false;
		};
	}

	mounted() {
		this.versionGroupModule.setDisabled(false);
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
		}
	}

	beforeDestroy() {
		this.tagSeachWorker?.terminate();
	}

	destroyed() {
		this.versionGroupModule.setDisabled(true);
	}

	@Watch("versionGroupModule.selectedGsVersion", { immediate: true })
	GsVersionUpdatedIm() {
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
		} else {
			this.noVersion = false;
		}
	}

	@Watch("versionGroupModule.selectedLsVersion")
	LsVersionUpdated() {
		if (!isAnyVersionSelected()) {
			this.noVersion = true;
		} else {
			this.noVersion = false;
		}
	}

	handleSearch(value: string) {
		this.tagSearch = [];
		this.searchTerm = value.trim();
		if (value.length == 0) {
			this.searching ? (this.searching = false) : null;
			return;
		}
		this.searching = true;
		new Promise((res) => {
			this.tagSeachWorker?.postMessage({ tags: this.tags, value });
			res();
		});
	}

	handleChange(id: number) {
		const value = this.tagSearch.find((f) => f.id === id)?.code as string;
		this.codeSelected = value;
	}

	fetchLFSearchReport() {
		this.loading = true;
		const tagCode = this.tagSearch.find((f) => f.code === this.codeSelected)?.code as string;
		const params: LFSerachReportRequestParamsModel = {
			tagCode: tagCode,
			gsVersionGroupId:
				VersionGroupModule.selectedGsVersion != "-1" ? VersionGroupModule.selectedGsVersion : null,
			lsVersionGroupId: VersionGroupModule.selectedLsVersion != "-1" ? VersionGroupModule.selectedLsVersion : null
		};
		this.lfSearchManagerService
			.getLFSearchReport(params)
			.then((data) => {
				this.lfSearchReport = data;
				this.codeSelected = "";
				this.loading = false;
			})
			.catch(() => {
				this.loading = false;
			});
	}

	get exportOptions() {
		return {
			formatCell: (cell: IWorkbookCell, binding: string, record: LFSearchReportResponseModel) => {
				if (binding === "sortUnit") {
					cell.value = record.displayName;
				}
			},
			filename: this.format(LFSearchReportLocale.LFSearchReportExportTitle)
		};
	}

	/*
	 * Runs when a key is pressed on Select dropdown.
	 * Since creating a form and adding submit event was not working on Select dropdown when enter was pressed,
	 * functionality for fetching report on enter keypress is achieved through this.
	 */
	onKeyPress(e) {
		if (e.keyCode === 13 && this.codeSelected !== "") {
			this.fetchLFSearchReport();
		}
	}
}
